import dayjs from 'dayjs';

// Specific color definitions for chart series
export const SERIES_COLORS = {
  primary: {
    border: '#2563eb', // Blue
    background: '#2563eb20'
  },
  secondary: {
    border: '#dc2626', // Red
    background: '#dc262620'
  },
  tertiary: {
    border: '#16a34a', // Green
    background: '#16a34a20'
  },
  quaternary: {
    border: '#ea580c', // Orange
    background: '#ea580c20'
  },
  quinary: {
    border: '#7c3aed', // Purple
    background: '#7c3aed20'
  },
  senary: {
    border: '#0891b2', // Cyan
    background: '#0891b220'
  },
  septenary: {
    border: '#d946ef', // Magenta/Fuchsia - distinctly different from red
    background: '#d946ef20'
  },
  octonary: {
    border: '#10b981', // Green variant
    background: '#10b98120'
  },
  nonary: {
    border: '#f59e0b', // Yellow
    background: '#f59e0b20'
  }
};

// Color palettes for charts (for backward compatibility and legacy usage)
export const CHART_COLORS = {
  // Primary border colors for chart lines
  borderColors: [
    SERIES_COLORS.primary.border,
    SERIES_COLORS.secondary.border,
    SERIES_COLORS.tertiary.border,
    SERIES_COLORS.quaternary.border,
    SERIES_COLORS.quinary.border,
    SERIES_COLORS.senary.border,
    SERIES_COLORS.septenary.border,
    SERIES_COLORS.octonary.border,
    SERIES_COLORS.nonary.border
  ],

  // Background colors with transparency for chart fills
  backgroundColors: [
    SERIES_COLORS.primary.background,
    SERIES_COLORS.secondary.background,
    SERIES_COLORS.tertiary.background,
    SERIES_COLORS.quaternary.background,
    SERIES_COLORS.quinary.background,
    SERIES_COLORS.senary.background,
    SERIES_COLORS.septenary.background,
    SERIES_COLORS.octonary.background,
    SERIES_COLORS.nonary.background
  ],

  // Grid and UI colors
  gridColor: '#e0e0e0',
  tooltipBackground: 'rgba(255, 255, 255, 0.95)',
  tooltipTitle: '#333',
  tooltipBody: '#333',
  tooltipBorder: '#ccc'
};

// Default chart styling values
export const CHART_DEFAULTS = {
  // Line styling
  borderWidth: 3,
  pointRadius: 4,
  pointHoverRadius: 6,
  tension: 0.2,
  fill: false,

  // Default height
  height: 400,

  // Font sizes
  legendFontSize: 11,
  tickFontSize: 10,

  // Legend configuration
  legend: {
    boxWidth: 12,
    boxHeight: 12,
    padding: 15
  },

  // Tooltip styling
  tooltip: {
    borderWidth: 1,
    cornerRadius: 4
  }
};

// UI Theme configuration for components
export const THEME_CONFIG = {
  paper: {
    elevation: 2,
    padding: 3,
    marginBottom: 2,
    border: "1px solid #d0d0d0"
  },

  chartContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
    minWidth: '100%',
    position: 'relative',
    paddingBottom: '20px' // Extra space for rotated labels
  },

  typography: {
    title: {
      variant: "h6",
      gutterBottom: true,
      marginBottom: 3
    },

    instruction: {
      variant: "body2",
      color: "text.secondary",
      marginBottom: 2,
      text: "Use mouse wheel to zoom, drag to pan horizontally."
    }
  }
};

// Chart.js options configuration factory
export const createChartOptions = (customOptions = {}) => {
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      title: {
        display: false
      },
      legend: {
        position: 'right',
        labels: {
          boxWidth: CHART_DEFAULTS.legend.boxWidth,
          boxHeight: CHART_DEFAULTS.legend.boxHeight,
          padding: CHART_DEFAULTS.legend.padding,
          font: {
            size: CHART_DEFAULTS.legendFontSize
          }
        }
      },
      datalabels: {
        display: false // Disable data labels for line charts
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: CHART_COLORS.tooltipBackground,
        titleColor: CHART_COLORS.tooltipTitle,
        bodyColor: CHART_COLORS.tooltipBody,
        borderColor: CHART_COLORS.tooltipBorder,
        borderWidth: CHART_DEFAULTS.tooltip.borderWidth,
        cornerRadius: CHART_DEFAULTS.tooltip.cornerRadius,
        displayColors: true,
        callbacks: {
          title: function (context) {
            // Format the date with day of week
            const date = new Date(context[0].parsed.x);
            const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
            const dateString = dayjs(date).format('DD-MM-YYYY');
            return `${dayName}, ${dateString}`;
          },
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'day',
          parser: 'YYYY-MM-DD',
          displayFormats: {
            day: 'DD-MM-YYYY'
          }
        },
        title: {
          display: false
        },
        grid: {
          display: true,
          color: CHART_COLORS.gridColor
        },
        ticks: {
          maxRotation: 90,
          minRotation: 90,
          font: {
            size: CHART_DEFAULTS.tickFontSize
          }
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: false
        },
        grid: {
          display: true,
          color: CHART_COLORS.gridColor
        }
      }
    }
  };

  // Merge custom options with default options
  return { ...defaultOptions, ...customOptions };
};

// Helper function to get series color by index (maps to specific named colors)
export const getSeriesColor = (index) => {
  const colorNames = [
    'primary', 'secondary', 'tertiary', 'quaternary', 'quinary',
    'senary', 'septenary', 'octonary', 'nonary'
  ];

  const colorName = colorNames[index % colorNames.length];
  return SERIES_COLORS[colorName];
};

// Helper function to format chart data
export const formatChartData = (data) => {
  return {
    datasets: data.map((series, index) => {
      const seriesColor = getSeriesColor(index);

      return {
        label: series.id,
        data: series.data.map(point => ({
          x: point.x,
          y: point.y
        })),
        borderColor: seriesColor.border,
        backgroundColor: seriesColor.background,
        borderWidth: CHART_DEFAULTS.borderWidth,
        pointRadius: CHART_DEFAULTS.pointRadius,
        pointHoverRadius: CHART_DEFAULTS.pointHoverRadius,
        tension: CHART_DEFAULTS.tension,
        fill: CHART_DEFAULTS.fill
      };
    })
  };
};

// Helper function to get color by index
export const getChartColor = (index, type = 'border') => {
  const colors = type === 'border' ? CHART_COLORS.borderColors : CHART_COLORS.backgroundColors;
  return colors[index % colors.length];
};

// Predefined chart themes for easy switching
export const CHART_THEMES = {
  default: {
    colors: CHART_COLORS,
    defaults: CHART_DEFAULTS
  },

  // You can add more themes here in the future
  dark: {
    colors: {
      ...CHART_COLORS,
      gridColor: '#404040',
      tooltipBackground: 'rgba(0, 0, 0, 0.9)',
      tooltipTitle: '#fff',
      tooltipBody: '#fff',
      tooltipBorder: '#666'
    },
    defaults: CHART_DEFAULTS
  },

  minimal: {
    colors: {
      ...CHART_COLORS,
      gridColor: '#f0f0f0'
    },
    defaults: {
      ...CHART_DEFAULTS,
      borderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 5
    }
  }
};

export default {
  SERIES_COLORS,
  CHART_COLORS,
  CHART_DEFAULTS,
  THEME_CONFIG,
  createChartOptions,
  formatChartData,
  getSeriesColor,
  getChartColor,
  CHART_THEMES
};
